import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { setLocale } from "../../app/i18n.js";
import { type SiteLang, siteText } from "./content.js";
import { SELLER } from "./seller.js";

/** Сайт показываем на сербском или английском; русский интерфейс видит английскую версию. */
export function useSiteLang(): SiteLang {
  const { i18n } = useTranslation();
  return i18n.language === "sr-Latn" ? "sr" : "en";
}

function LangSwitch() {
  const lang = useSiteLang();
  const btn = (l: SiteLang, label: string) => (
    <button
      type="button"
      onClick={() => setLocale(l === "sr" ? "sr-Latn" : "en")}
      className={`rounded-lg px-2 py-1 text-sm ${lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1">
      {btn("sr", "SR")}
      {btn("en", "EN")}
    </div>
  );
}

/** Шапка и подвал сайта; ссылки на Terms, Refund и Privacy всегда видны (требование Paddle). */
export function SiteLayout({ children }: { children?: ReactNode }) {
  const lang = useSiteLang();
  const t = siteText[lang];
  const { pathname, hash } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname нужен, при переходе между страницами прокручиваем наверх.
  useEffect(() => {
    // Якорь (/privacy#data-deletion) после отрисовки; иначе наверх страницы.
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  const link = ({ isActive }: { isActive: boolean }) => `text-sm ${isActive ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"}`;
  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="font-semibold">
            {SELLER.productName}
          </Link>
          <nav className="flex flex-wrap items-center gap-4">
            <NavLink to="/pricing" className={link}>
              {t.nav.pricing}
            </NavLink>
            <Link to="/login" className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white">
              {t.nav.login}
            </Link>
            <LangSwitch />
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8">{children ?? <Outlet />}</main>
      <footer className="border-t border-slate-100">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-slate-500">
          <span>
            © 2026 {SELLER.legalName}. {t.footer.rights}
          </span>
          <nav className="flex gap-4">
            <NavLink to="/pricing" className={link}>
              {t.nav.pricing}
            </NavLink>
            <NavLink to="/terms" className={link}>
              {t.nav.terms}
            </NavLink>
            <NavLink to="/refund" className={link}>
              {t.nav.refund}
            </NavLink>
            <NavLink to="/privacy" className={link}>
              {t.nav.privacy}
            </NavLink>
          </nav>
        </div>
      </footer>
    </div>
  );
}
